import React, { useMemo, useState } from 'react';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import { OrgChartRow, isManagerEmail } from '../../../support/orgScope';
import type { DateWindow } from '../../../support/campaignLeader';
import { CAMPAIGN_PAGE_CAP, useCampaignRollupData } from '../../../hooks/useCampaignRollupData';
import { useCampaignParticipationRollup, useJourneyMapRollup } from '../../../hooks/useCampaignRollups';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';
import CampaignsDashboardSections from './CampaignsDashboardSections';
import CampaignsParticipationSections from './CampaignsParticipationSections';
import CampaignsTeamMembersTable from './CampaignsTeamMembersTable';

/**
 * Leader > Campaigns > Team. The campaign rollup for a manager's org subtree.
 *
 * The dashboard (Deliveries, Enablement progress) and the "By Team Member" table are fed by ONE source
 * (`useCampaignRollupData`): the server rollups, which are uncapped, scoped to the selected campaign and
 * fiscal window, and summed over each person's subtree. They therefore always describe the same campaign
 * and period. Clicking a report re-roots the page on them.
 */

interface Props {
  orgRows: OrgChartRow[];
  managerEmail: string;
  onSelectPerson: (email: string) => void;
  /** When set, scope every rollup to this one campaign; undefined = across all campaigns. */
  campaignId?: number;
  /** Human campaign name for the per-member reminder card. */
  campaignName?: string;
  /** Inclusive fiscal window from the time dropdown; null/undefined = all time. */
  timeWindow?: DateWindow | null;
  /** The period in words (e.g. "This Fiscal Quarter"), for headings. */
  timeShort?: string;
  /** The fiscal calendar is still loading; hold the figures until the window is known. */
  timePending?: boolean;
}

export default function CampaignsTeamSummaryPage({
  orgRows,
  managerEmail,
  onSelectPerson,
  campaignId,
  campaignName,
  timeWindow = null,
  timeShort,
  timePending = false,
}: Props) {
  const [isTeamOpen, setTeamOpen] = useState(true);

  const manager = String(managerEmail || '').toLowerCase();
  const isMgr = useMemo(() => isManagerEmail(orgRows, manager), [orgRows, manager]);

  const data = useCampaignRollupData({
    manager,
    orgRows,
    campaignId,
    window: timeWindow,
    enabled: isMgr && !timePending,
  });

  // Participation reports for everyone in the org: the funnel (enrolled, enablement, deliveries) and the
  // journey maps by map. Both are server rollups scoped to the leader's org; neither takes a user list.
  const participationQ = useCampaignParticipationRollup({
    manager,
    campaignId,
    window: timeWindow,
    enabled: isMgr && !timePending,
  });
  const mapsQ = useJourneyMapRollup({ manager, campaignId, enabled: isMgr });

  // Name the scope of every number: the selected campaign (else "All campaigns") and the period.
  const campaignScope = campaignId != null ? campaignName || `Campaign ${campaignId}` : 'All campaigns';
  const scopeLabel = timeWindow && timeShort ? `${campaignScope} · ${timeShort}` : campaignScope;

  if (!isMgr) {
    return <div className="hbr-css__text-secondary-sm">Selected user is not a manager.</div>;
  }
  if (data.error) {
    return <div style={{ padding: 12, color: 'red' }}>{data.error.message}</div>;
  }

  const loading = timePending || data.deliveriesLoading || !data.deliveries || !data.pipeline;

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      {data.mayBeIncomplete && (
        <div style={{ fontSize: '0.8125rem', color: '#b45309' }}>
          Campaign data is capped at {CAMPAIGN_PAGE_CAP.toLocaleString()} records per table, so these totals may
          be incomplete. Pick a single campaign for exact figures.
        </div>
      )}

      {loading || !data.deliveries || !data.pipeline ? (
        <HbrSpinner>Loading campaigns…</HbrSpinner>
      ) : (
        <CampaignsDashboardSections
          deliveries={data.deliveries}
          pipeline={data.pipeline}
          enrollment={data.enrollment}
          enrollmentLoading={data.enrollmentLoading}
          scopeLabel={scopeLabel}
          keyPrefix={`camp-team-${manager}-${campaignId ?? 'all'}-${timeWindow?.start ?? 'all'}`}
          enablementNote={timeWindow ? 'Enrollment progress is not filtered by the time period.' : undefined}
        />
      )}

      <CampaignsParticipationSections
        orgRows={orgRows}
        manager={manager}
        participation={{
          data: participationQ.data,
          loading: timePending || participationQ.isLoading,
          error: (participationQ.error as Error | null) ?? null,
        }}
        maps={{
          data: mapsQ.data,
          loading: mapsQ.isLoading,
          error: (mapsQ.error as Error | null) ?? null,
        }}
        scopeLabel={scopeLabel}
        campaignScope={campaignScope}
        periodActive={!!timeWindow}
        keyPrefix={`camp-part-${manager}-${campaignId ?? 'all'}-${timeWindow?.start ?? 'all'}`}
      />

      <StbAccordion
        variant="minimal"
        label={`By Team Member — ${scopeLabel}`}
        isOpen={isTeamOpen}
        toggleIsOpen={setTeamOpen}
      >
        <div style={{ minHeight: 200, padding: 12 }}>
          {loading ? (
            <HbrSpinner>Loading deliveries…</HbrSpinner>
          ) : (
            <CampaignsTeamMembersTable
              orgRows={orgRows}
              managerEmail={manager}
              deliveryFor={data.deliveryFor}
              byUser={data.byUser}
              cohortsByEmail={data.cohortsByEmail}
              campaignName={scopeLabel}
              onSelectEmail={onSelectPerson}
            />
          )}
        </div>
      </StbAccordion>
    </div>
  );
}
